import { NcListBox, NcLoadingOverlay } from "@noted/noted-components";
import { HTMLAttributes } from "react";

import { Context, isDarkNotedUser, isOrgAdmin } from "../../api/context-api";
import { NotedAdminIcon, OrgAdminIcon, WorkerIcon } from "../../components/icon";

function not<T>(fn: (arg: T) => boolean) {
  return (arg: T) => !fn(arg);
}

function sortByUserName(a: Context, b: Context) {
  return a.name.localeCompare(b.name);
}

function groupByOrganisation(contexts: Context[]) {
  const groupsObj = contexts.reduce<Record<string, { name: string; contexts: Context[] }>>(
    (groups, context) => {
      if (!context.organisation) {
        return groups;
      }
      const orgId = context.organisation.id;
      if (!groups[orgId]) {
        groups[orgId] = {
          name: context.organisation.name,
          contexts: [],
        };
      }
      groups[orgId].contexts.push(context);
      return groups;
    },
    {}
  );

  // Convert to array and sort by organisation name
  return Object.values(groupsObj).sort((a, b) =>
    a.name.toLowerCase().localeCompare(b.name.toLowerCase())
  );
}

function ContextList({
  contexts,
  onSelect,
  isLoading,
}: {
  contexts: Context[];
  onSelect: (context: Context) => void;
  isLoading?: boolean;
}) {
  const darkNotedContexts = contexts?.filter(isDarkNotedUser).sort(sortByUserName) ?? [];
  const appUserContexts = contexts?.filter(not(isDarkNotedUser)).sort(sortByUserName) ?? [];
  const groupedAppUsers = groupByOrganisation(appUserContexts);

  return (
    <div className="grid gap-6">
      {darkNotedContexts?.length ? (
        <Section data-testid="dark-noted-user-section" isLoading={isLoading}>
          <header>
            <SectionHeading>Dark Noted</SectionHeading>
          </header>
          <NcListBox variant="borderless" items={darkNotedContexts} aria-label="Dark Noted users">
            {context => {
              const textValue = `${context.name} ${context.isSuspended ? " (suspended user)" : ""}`;
              return (
                <NcListBox.Item
                  onAction={() => onSelect(context)}
                  textValue={textValue}
                  isDisabled={context.isSuspended}
                >
                  <NotedAdminIcon className="shrink-0" />

                  <span className="font-semibold">{context.name}</span>
                  {context.isSuspended && (
                    <>
                      <span>-</span>
                      <span className="italic">Suspended</span>
                    </>
                  )}
                </NcListBox.Item>
              );
            }}
          </NcListBox>
        </Section>
      ) : null}

      {groupedAppUsers.map(({ name: orgName, contexts: orgContexts }, index) => (
        <Section key={orgName} data-testid={`org-section-${index}`} isLoading={isLoading}>
          <header>
            <SectionHeading>{orgName}</SectionHeading>
          </header>
          <NcListBox
            variant="borderless"
            items={orgContexts}
            aria-label={`Organisation ${orgName} users`}
          >
            {context => {
              const textValue = `${context.name} ${context.isSuspended ? " (suspended user)" : ""}`;
              return (
                <NcListBox.Item
                  onAction={() => onSelect(context)}
                  textValue={textValue}
                  isDisabled={context.isSuspended}
                >
                  {isOrgAdmin(context) ? (
                    <OrgAdminIcon className="shrink-0" />
                  ) : (
                    <WorkerIcon className="shrink-0" />
                  )}

                  <span className="font-semibold">{context.name}</span>
                  {context.isSuspended && (
                    <>
                      <span>-</span>
                      <span className="italic">Suspended</span>
                    </>
                  )}
                </NcListBox.Item>
              );
            }}
          </NcListBox>
        </Section>
      ))}
    </div>
  );
}

export default ContextList;

interface SectionProps extends HTMLAttributes<HTMLElement> {
  isLoading?: boolean;
}

const Section = ({ children, isLoading, ...props }: SectionProps) => (
  <section className="bg-light text-dark rounded-sm shadow-xl" {...props}>
    <NcLoadingOverlay isLoading={isLoading}>{children}</NcLoadingOverlay>
  </section>
);

const SectionHeading = ({ ...props }) => (
  <h2 className="border-ui border-b px-4 pt-3 pb-2 text-xl font-bold" {...props} />
);
